import { createContext, useContext, useState, useCallback, ReactNode, useMemo } from 'react'
import { Language, TranslationKeys, translations } from './translations'

interface LanguageInfo {
  code: Language
  name: string
}

interface I18nContextType {
  language: Language
  setLanguage: (lang: Language) => void
  availableLanguages: LanguageInfo[]
  t: (key: keyof TranslationKeys, params?: Record<string, string | number>) => string
}

const I18nContext = createContext<I18nContextType | null>(null)

const STORAGE_KEY = 'preferred-language'

const availableLanguages: LanguageInfo[] = [
  { code: 'en', name: 'English' },
  { code: 'de', name: 'Deutsch' },
]

function getInitialLanguage(): Language {
  if (typeof window === 'undefined') return 'en'
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored === 'en' || stored === 'de') return stored
  const browserLang = navigator.language.split('-')[0]
  if (browserLang === 'de') return 'de'
  return 'en'
}

interface I18nProviderProps {
  children: ReactNode
}

export function I18nProvider({ children }: I18nProviderProps) {
  const [language, setLanguageState] = useState<Language>(getInitialLanguage)

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang)
    localStorage.setItem(STORAGE_KEY, lang)
  }, [])

  const t = useCallback(
    (key: keyof TranslationKeys, params?: Record<string, string | number>): string => {
      const translation = translations[language][key]
      if (!translation) return key

      if (!params) return translation

      return Object.entries(params).reduce(
        (result, [paramKey, value]) => result.replace(`{${paramKey}}`, String(value)),
        translation
      )
    },
    [language]
  )

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      availableLanguages,
      t,
    }),
    [language, setLanguage, t]
  )

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n(): I18nContextType {
  const context = useContext(I18nContext)
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider')
  }
  return context
}
